"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { SitecoreLink as JssLink } from "../../atoms/SitecoreLink";
import Button, { buttonVariants } from "../../atoms/Button";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import { LinkProps } from "./link.props";

const getAlignmentStyles = (styles: string[] = []): string => {
  if (!styles || styles.length === 0) return "";
  const resolvedPosition = styles.find((cls) =>
    ["position-left", "position-center", "position-right"].includes(cls),
  );
  switch (resolvedPosition) {
    case "position-center":
      return "flex justify-center";
    case "position-right":
      return "flex justify-end";
    case "position-left":
      return "flex justify-start";
    default:
      return "";
  }
};

export const Default = (props: LinkProps) => {
  console.log("---->[Link] Component", props.fields);

  if (props.params?.FieldNames === "CookieSettings") {
    return <CookieSettings {...props} />;
  }
  if (props.params?.FieldNames === "ContactUsSideButton") {
    return <ContactUsSideButton {...props} />;
  }

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Link" />;
  }

  const stylesArray = params?.Styles ? params.Styles.split(" ") : [];
  let buttonType =
    stylesArray.length > 0
      ? buttonVariants.find((cls) => stylesArray.includes(cls)) ||
        "primary-reverse"
      : "primary-reverse";
  if (fields.Link?.value?.class) buttonType = fields.Link.value.class;

  const hasLinkStyle = stylesArray.includes("link-style");
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`link-component link ${params?.Styles?.replaceAll(buttonType, "") ?? ""} ${
        hasLinkStyle ? "flex justify-center mb-8" : ""
      } ${getAlignmentStyles(stylesArray)} medium-indent-spacing ${hiddenBlockStyles}`}
      data-role="link-component"
    >
      <div className="component-content">
        <div className="link-container" data-testid="default-link">
          <Button
            buttonType={buttonType}
            field={fields.Link}
            data-role="link"
            className={`w-fit ${buttonType?.startsWith("tertiary") ? "!min-w-0" : ""} ${
              buttonType.startsWith("secondary") ? "btn-secondary " : ""
            }`}
            iconName="CHEVRON_BLUE_RIGHT"
          />
        </div>
      </div>
    </div>
  );
};

export const CookieSettings = (props: LinkProps) => {
  console.log("---->[Link:CookieSettings] Component", props.fields);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Link" />;
  }

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`link-cookiesettings-component ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-role="link-component"
    >
      <div className="component-content" data-testid="cookie-settings-link">
        <Button
          buttonType={fields.Link?.value?.class || "primary-reverse"}
          field={fields.Link}
          id="ot-sdk-btn"
          iconName=""
          className={`ot-sdk-show-settings text-[11.2px] font-bold !leading-[13.44px] !h-auto !min-h-auto lg:hover:underline hover:!text-dark-blue !w-auto max-w-max active:text-white border-[0.5px] border-[var(--color-muted-green)] active:[background:var(--color-ecolab-gradient-blue)] ${params?.Styles ?? ""}`}
          data-role="link"
        />
      </div>
    </div>
  );
};

export const ContactUsSideButton = (props: LinkProps) => {
  console.log("---->[Link:ContactUsSideButton] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;
  const pathname = usePathname();

  const [isContactUsFormPresent, setIsContactUsFormPresent] = useState(false);

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  // Cheap derivation; let React Compiler memoize. Manual useMemo here was
  // skipped by the compiler due to a deps mismatch.
  const href = fields?.Link?.value?.href;
  const formName = href?.includes("#") ? href.split("#")[1] : "formName";

  const scrollTo = useCallback(() => {
    const element = document.getElementsByName(formName)[0] as
      | HTMLInputElement
      | undefined;
    const el = element?.closest('[data-evg-component="_mcp-call-to-action"]');
    if (!el) return;

    const prefersReduced = globalThis.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const behavior: ScrollBehavior = prefersReduced ? "auto" : "smooth";

    el.scrollIntoView({ behavior, block: "start" });
  }, [formName]);

  useEffect(() => {
    if (isPageEditing) return;

    // Reset on mount or pathname change so the button hides during navigation,
    // then poll for the contact form on the new page. The setState here syncs
    // visibility to an external (DOM-presence) signal — there's no
    // render-time alternative.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsContactUsFormPresent(false);

    let retries = 0;
    const maxRetries = 20;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const checkFormExists = () => {
      if (cancelled) return;
      const form = document.getElementsByTagName("form")[0];
      if (form) {
        setIsContactUsFormPresent(true);
      } else if (retries < maxRetries) {
        retries++;
        timeoutId = setTimeout(checkFormExists, 500);
      } else {
        setIsContactUsFormPresent(false);
      }
    };

    checkFormExists();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isPageEditing, formName, pathname]);

  if (!fields) {
    return <NoDataFallback componentName="Link" />;
  }

  if (!isContactUsFormPresent && !isPageEditing) return <></>;

  return (
    <div
      className={`contactus-btn-component ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-role="link-component"
    >
      {isPageEditing ? (
        <div className="p-10">
          <JssLink field={fields.Link} />
        </div>
      ) : (
        <div
          className="component-content fixed top-auto bottom-0 right-4 z-[9] lg:top-[50%] lg:bottom-auto lg:right-0 lg:origin-right lg:transform-[rotate(-90deg)_translate(50%,-20px)]"
          data-testid="contact-us-btn"
        >
          <button
            className="cursor-pointer flex gap-x-2 items-center text-neutral-900 px-3 py-2 rounded-t-xl bg-teal-300"
            aria-label={fields.Link?.value?.text}
            onClick={scrollTo}
          >
            <Icon icon={ICONS.EMAIL} className="transform-[rotate(90deg)]" />
            <span className="text-base font-medium">
              {fields.Link?.value?.text}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};
